/* Mock data for Automated Workflows */

import {
  WorkflowTriggerDto,
  WorkflowConditionFieldDto,
  WorkflowActionDto,
  AutomatedWorkflowDto,
  WorkflowUserDto,
  WorkflowRoleDto,
  WorkflowOrderTypeDto,
  WorkflowSupplierDto,
  WorkflowStockListDto,
  WorkflowZoneDto,
  WorkflowReportTypeDto,
  WorkflowTaskTemplateDto,
} from './workflowsTypes';

/* ---- Triggers ---- */

export const mockTriggers: WorkflowTriggerDto[] = [
  { id: 'task-completed', name: 'Task Completed', description: 'Fires when a task is marked as completed', type: 'TaskCompleted', category: 'Tasks' },
  { id: 'task-overdue', name: 'Task Overdue', description: 'Fires when a task becomes overdue', type: 'TaskOverdue', category: 'Tasks' },
  { id: 'task-assigned', name: 'Task Assigned', description: 'Fires when a task is assigned to a user or role', type: 'TaskAssigned', category: 'Tasks' },
  { id: 'order-placed', name: 'Order Placed', description: 'Fires when an order is submitted', type: 'OrderPlaced', category: 'Orders' },
  { id: 'order-received', name: 'Order Received', description: 'Fires when goods are receipted', type: 'OrderReceived', category: 'Orders' },
  { id: 'order-cancelled', name: 'Order Cancelled', description: 'Fires when an order is cancelled', type: 'OrderCancelled', category: 'Orders' },
  { id: 'stock-count-completed', name: 'Stock Count Completed', description: 'Fires when a stock count is completed', type: 'StockCountCompleted', category: 'Stock' },
  { id: 'stock-count-overdue', name: 'Stock Count Overdue', description: 'Fires when a scheduled stock count is overdue', type: 'StockCountOverdue', category: 'Stock' },
  { id: 'stock-level-low', name: 'Stock Level Low', description: 'Fires when stock falls below minimum level', type: 'StockLevelLow', category: 'Stock' },
  { id: 'stock-level-critical', name: 'Stock Level Critical', description: 'Fires when stock falls to critical level', type: 'StockLevelCritical', category: 'Stock' },
  { id: 'transfer-created', name: 'Transfer Created', description: 'Fires when a stock transfer is created', type: 'TransferCreated', category: 'Transfers' },
  { id: 'transfer-completed', name: 'Transfer Completed', description: 'Fires when a stock transfer is completed', type: 'TransferCompleted', category: 'Transfers' },
  { id: 'waste-recorded', name: 'Waste Recorded', description: 'Fires when waste is logged', type: 'WasteRecorded', category: 'Waste' },
  { id: 'receipt-processed', name: 'Receipt Processed', description: 'Fires when a goods receipt is processed', type: 'ReceiptProcessed', category: 'Receipts' },
  { id: 'invoice-received', name: 'Invoice Received', description: 'Fires when an invoice is received', type: 'InvoiceReceived', category: 'Invoices' },
  { id: 'scheduled-time', name: 'Scheduled Time', description: 'Fires at a specified time', type: 'ScheduledTime', category: 'Schedule' },
  { id: 'manual', name: 'Manual Trigger', description: 'Triggered manually by a user', type: 'Manual', category: 'Manual' },
  // IOT & Sensor Triggers
  { id: 'temperature-alert', name: 'Temperature Alert', description: 'Fires when a temperature sensor reports an alert', type: 'TemperatureAlert', category: 'IOT Sensors' },
  { id: 'temperature-out-of-range', name: 'Temperature Out of Range', description: 'Fires when temperature goes outside acceptable range', type: 'TemperatureOutOfRange', category: 'IOT Sensors' },
  { id: 'scale-reading', name: 'Scale Reading', description: 'Fires when a scale records a measurement', type: 'ScaleReading', category: 'IOT Sensors' },
  { id: 'scale-threshold-breached', name: 'Scale Threshold Breached', description: 'Fires when weight exceeds or falls below threshold', type: 'ScaleThresholdBreached', category: 'IOT Sensors' },
  { id: 'cctv-motion-detected', name: 'CCTV Motion Detected', description: 'Fires when motion is detected on CCTV', type: 'CCTVMotionDetected', category: 'CCTV & Cameras' },
  { id: 'cctv-alert-triggered', name: 'CCTV Alert Triggered', description: 'Fires when a CCTV security alert is triggered', type: 'CCTVAlertTriggered', category: 'CCTV & Cameras' },
  { id: 'camera-snapshot', name: 'Camera Snapshot', description: 'Fires when a camera captures a scheduled or event snapshot', type: 'CameraSnapshot', category: 'CCTV & Cameras' },
  { id: 'door-sensor-triggered', name: 'Door Sensor Triggered', description: 'Fires when a door sensor detects open/close', type: 'DoorSensorTriggered', category: 'IOT Sensors' },
  { id: 'equipment-malfunction', name: 'Equipment Malfunction', description: 'Fires when equipment reports a malfunction', type: 'EquipmentMalfunction', category: 'Equipment' },
];

/* ---- Condition Fields ---- */

export const mockConditionFields: WorkflowConditionFieldDto[] = [
  // Task conditions - only for task triggers
  { id: 'task-category', name: 'Task Category', description: 'The category of the task', field: 'TaskCategory', dataType: 'enum', enumValues: ['Opening', 'Closing', 'Cleaning', 'Safety', 'Training', 'Compliance', 'Administrative'], category: 'Tasks', applicableTriggers: ['TaskCompleted', 'TaskOverdue', 'TaskAssigned'] },
  { id: 'task-priority', name: 'Task Priority', description: 'The priority level of the task', field: 'TaskPriority', dataType: 'enum', enumValues: ['Low', 'Medium', 'High', 'Emergency'], category: 'Tasks', applicableTriggers: ['TaskCompleted', 'TaskOverdue', 'TaskAssigned'] },
  { id: 'task-status', name: 'Task Status', description: 'The current status of the task', field: 'TaskStatus', dataType: 'enum', enumValues: ['Open', 'InProgress', 'PendingApproval', 'Approved', 'Rejected', 'Completed'], category: 'Tasks', applicableTriggers: ['TaskCompleted', 'TaskOverdue', 'TaskAssigned'] },
  // Order conditions - only for order triggers
  { id: 'order-type', name: 'Order Type', description: 'The type of order', field: 'OrderType', dataType: 'enum', enumValues: ['Standard', 'Rush', 'Autoshipment', 'Manual'], category: 'Orders', applicableTriggers: ['OrderPlaced', 'OrderReceived', 'OrderCancelled'] },
  { id: 'order-value', name: 'Order Value', description: 'The total value of the order', field: 'OrderValue', dataType: 'number', category: 'Orders', applicableTriggers: ['OrderPlaced', 'OrderReceived', 'OrderCancelled'] },
  { id: 'order-item-count', name: 'Order Item Count', description: 'Number of items in the order', field: 'OrderItemCount', dataType: 'number', category: 'Orders', applicableTriggers: ['OrderPlaced', 'OrderReceived', 'OrderCancelled'] },
  // Generic conditions - apply to all triggers
  { id: 'store-number', name: 'Store Number', description: 'The store number', field: 'StoreNumber', dataType: 'string', category: 'Store' },
  { id: 'store-name', name: 'Store Name', description: 'The name of the store', field: 'StoreName', dataType: 'string', category: 'Store' },
  // Stock conditions
  { id: 'stock-level', name: 'Stock Level', description: 'Current stock level', field: 'StockLevel', dataType: 'number', category: 'Stock', applicableTriggers: ['StockLevelLow', 'StockLevelCritical', 'StockCountCompleted', 'StockCountOverdue'] },
  { id: 'product-category', name: 'Product Category', description: 'The category of the product', field: 'ProductCategory', dataType: 'string', category: 'Products', applicableTriggers: ['StockLevelLow', 'StockLevelCritical', 'OrderPlaced', 'WasteRecorded'] },
  { id: 'product-name', name: 'Product Name', description: 'The name of the product', field: 'ProductName', dataType: 'string', category: 'Products', applicableTriggers: ['StockLevelLow', 'StockLevelCritical', 'OrderPlaced', 'WasteRecorded'] },
  // Transfer conditions
  { id: 'transfer-type', name: 'Transfer Type', description: 'Type of stock transfer', field: 'TransferType', dataType: 'enum', enumValues: ['Inbound', 'Outbound', 'Internal'], category: 'Transfers', applicableTriggers: ['TransferCreated', 'TransferCompleted'] },
  { id: 'transfer-status', name: 'Transfer Status', description: 'Status of the transfer', field: 'TransferStatus', dataType: 'enum', enumValues: ['Pending', 'InTransit', 'Completed', 'Cancelled'], category: 'Transfers', applicableTriggers: ['TransferCreated', 'TransferCompleted'] },
  // Waste conditions
  { id: 'waste-category', name: 'Waste Category', description: 'Category of waste', field: 'WasteCategory', dataType: 'enum', enumValues: ['Expired', 'Damaged', 'Spillage', 'Temperature', 'Other'], category: 'Waste', applicableTriggers: ['WasteRecorded'] },
  { id: 'waste-value', name: 'Waste Value', description: 'Value of the waste', field: 'WasteValue', dataType: 'number', category: 'Waste', applicableTriggers: ['WasteRecorded'] },
  // Time conditions - apply to scheduled and general triggers
  { id: 'day-of-week', name: 'Day of Week', description: 'Day of the week', field: 'DayOfWeek', dataType: 'enum', enumValues: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'], category: 'Time' },
  { id: 'time-of-day', name: 'Time of Day', description: 'Time of day (HH:mm)', field: 'TimeOfDay', dataType: 'string', category: 'Time' },
  { id: 'user-role', name: 'User Role', description: 'Role of the user', field: 'UserRole', dataType: 'enum', enumValues: ['Store Manager', 'Assistant Manager', 'Team Member', 'Admin'], category: 'User' },
  // IOT Temperature Sensor conditions
  { id: 'temperature', name: 'Temperature', description: 'Temperature reading in Celsius', field: 'Temperature', dataType: 'number', category: 'IOT Sensors', applicableTriggers: ['TemperatureAlert', 'TemperatureOutOfRange'] },
  { id: 'temperature-zone', name: 'Temperature Zone', description: 'Zone where temperature sensor is located', field: 'TemperatureZone', dataType: 'enum', enumValues: ['Walk-in Cooler', 'Walk-in Freezer', 'Display Fridge', 'Kitchen', 'Prep Area', 'Storage'], category: 'IOT Sensors', applicableTriggers: ['TemperatureAlert', 'TemperatureOutOfRange'] },
  { id: 'temperature-sensor-id', name: 'Temperature Sensor ID', description: 'Unique identifier of the temperature sensor', field: 'TemperatureSensorId', dataType: 'string', category: 'IOT Sensors', applicableTriggers: ['TemperatureAlert', 'TemperatureOutOfRange'] },
  // IOT Scale conditions
  { id: 'scale-weight', name: 'Scale Weight', description: 'Weight reading from scale in kg', field: 'ScaleWeight', dataType: 'number', category: 'IOT Sensors', applicableTriggers: ['ScaleReading', 'ScaleThresholdBreached'] },
  { id: 'scale-id', name: 'Scale ID', description: 'Unique identifier of the scale', field: 'ScaleId', dataType: 'string', category: 'IOT Sensors', applicableTriggers: ['ScaleReading', 'ScaleThresholdBreached'] },
  { id: 'scale-location', name: 'Scale Location', description: 'Location of the scale', field: 'ScaleLocation', dataType: 'enum', enumValues: ['Kitchen', 'Prep Area', 'Receiving', 'Waste Station'], category: 'IOT Sensors', applicableTriggers: ['ScaleReading', 'ScaleThresholdBreached'] },
  // CCTV & Camera conditions
  { id: 'camera-id', name: 'Camera ID', description: 'Unique identifier of the camera', field: 'CameraId', dataType: 'string', category: 'CCTV & Cameras', applicableTriggers: ['CCTVMotionDetected', 'CCTVAlertTriggered', 'CameraSnapshot'] },
  { id: 'camera-zone', name: 'Camera Zone', description: 'Zone covered by the camera', field: 'CameraZone', dataType: 'enum', enumValues: ['Entrance', 'Exit', 'Cash Register', 'Kitchen', 'Storage', 'Parking', 'Dining Area'], category: 'CCTV & Cameras', applicableTriggers: ['CCTVMotionDetected', 'CCTVAlertTriggered', 'CameraSnapshot'] },
  { id: 'motion-detected', name: 'Motion Detected', description: 'Whether motion was detected', field: 'MotionDetected', dataType: 'boolean', category: 'CCTV & Cameras', applicableTriggers: ['CCTVMotionDetected'] },
  { id: 'alert-severity', name: 'Alert Severity', description: 'Severity level of the alert', field: 'AlertSeverity', dataType: 'enum', enumValues: ['Low', 'Medium', 'High', 'Critical'], category: 'IOT Sensors', applicableTriggers: ['TemperatureAlert', 'CCTVAlertTriggered', 'EquipmentMalfunction'] },
  // Equipment conditions
  { id: 'equipment-id', name: 'Equipment ID', description: 'Unique identifier of the equipment', field: 'EquipmentId', dataType: 'string', category: 'Equipment', applicableTriggers: ['EquipmentMalfunction'] },
  { id: 'equipment-type', name: 'Equipment Type', description: 'Type of equipment', field: 'EquipmentType', dataType: 'enum', enumValues: ['Fridge', 'Freezer', 'Oven', 'Fryer', 'Grill', 'HVAC', 'POS Terminal', 'Display Screen'], category: 'Equipment', applicableTriggers: ['EquipmentMalfunction'] },
  { id: 'sensor-battery-level', name: 'Sensor Battery Level', description: 'Battery level of wireless sensor (%)', field: 'SensorBatteryLevel', dataType: 'number', category: 'IOT Sensors', applicableTriggers: ['TemperatureAlert', 'ScaleReading', 'DoorSensorTriggered'] },
];

/* ---- Actions ---- */

export const mockActions: WorkflowActionDto[] = [
  { id: 'create-task', name: 'Create Task', description: 'Creates a new task from a template', type: 'CreateTask', category: 'Tasks', requiredParams: ['taskTemplateId'], optionalParams: ['assignToUserId', 'assignToRoleId', 'priority', 'dueDate'] },
  { id: 'assign-task', name: 'Assign Task', description: 'Assigns a task to a user or role', type: 'AssignTask', category: 'Tasks', requiredParams: [], optionalParams: ['assignToUserId', 'assignToRoleId'] },
  { id: 'escalate-task', name: 'Escalate Task', description: 'Escalates a task to a higher priority or manager', type: 'EscalateTask', category: 'Tasks', requiredParams: [], optionalParams: ['escalateToUserId', 'escalateToRoleId', 'newPriority'] },
  { id: 'send-notification', name: 'Send Notification', description: 'Sends an in-app notification', type: 'SendNotification', category: 'Notifications', requiredParams: ['title', 'message'], optionalParams: ['recipientUserIds', 'recipientRoleIds'] },
  { id: 'send-email', name: 'Send Email', description: 'Sends an email notification', type: 'SendEmail', category: 'Notifications', requiredParams: ['subject', 'body'], optionalParams: ['recipientEmails', 'recipientUserIds'] },
  { id: 'send-sms', name: 'Send SMS', description: 'Sends an SMS notification', type: 'SendSMS', category: 'Notifications', requiredParams: ['message'], optionalParams: ['recipientPhones', 'recipientUserIds'] },
  { id: 'create-order', name: 'Create Order', description: 'Creates a new order', type: 'CreateOrder', category: 'Orders', requiredParams: ['orderTypeId'], optionalParams: ['supplierId', 'items'] },
  { id: 'approve-order', name: 'Approve Order', description: 'Automatically approves an order', type: 'ApproveOrder', category: 'Orders', requiredParams: [], optionalParams: ['approverUserId'] },
  { id: 'cancel-order', name: 'Cancel Order', description: 'Cancels an order', type: 'CancelOrder', category: 'Orders', requiredParams: [], optionalParams: ['reason'] },
  { id: 'create-transfer', name: 'Create Transfer', description: 'Creates a stock transfer request', type: 'CreateTransfer', category: 'Transfers', requiredParams: ['destinationStoreNumber'], optionalParams: ['items', 'notes'] },
  { id: 'create-stock-count', name: 'Create Stock Count', description: 'Creates a stock count', type: 'CreateStockCount', category: 'Stock', requiredParams: [], optionalParams: ['stockListId', 'zoneId', 'dueDate'] },
  { id: 'generate-report', name: 'Generate Report', description: 'Generates a report', type: 'GenerateReport', category: 'Reports', requiredParams: ['reportType'], optionalParams: ['dateRange', 'format', 'recipients'] },
  { id: 'update-stock-level', name: 'Update Stock Level', description: 'Updates stock level for an item', type: 'UpdateStockLevel', category: 'Stock', requiredParams: ['itemNumber', 'adjustment'], optionalParams: ['reason'] },
  { id: 'log-event', name: 'Log Event', description: 'Logs an event for auditing', type: 'LogEvent', category: 'Audit', requiredParams: ['eventType', 'message'], optionalParams: ['metadata'] },
  { id: 'webhook-call', name: 'Webhook Call', description: 'Calls an external webhook', type: 'WebhookCall', category: 'Integration', requiredParams: ['url'], optionalParams: ['method', 'headers', 'body'] },
];

/* ---- Workflows ---- */

export const mockWorkflows: AutomatedWorkflowDto[] = [
  {
    id: 1,
    name: 'High Value Waste Alert',
    description: 'Notifies manager when waste value exceeds threshold',
    franchiseId: 1,
    storeNumbers: null,
    isActive: true,
    trigger: 'WasteRecorded',
    conditionPair1: {
      id: 1,
      criteria1: { id: 1, field: 'WasteValue', operator: 'GreaterThan', value: '100' },
      criteria2: null,
      criteriaLogic: 'AND',
    },
    conditionPair2: null,
    conditionPairLogic: 'AND',
    action: { type: 'SendNotification', params: { title: 'High Value Waste Alert', message: 'Waste recorded exceeds £100', recipientRoleIds: '1' } },
    createdAtUtc: '2024-11-01T10:00:00Z',
    createdByUserId: 1,
    updatedAtUtc: null,
    updatedByUserId: null,
    lastTriggeredAtUtc: '2024-11-20T14:30:00Z',
    triggerCount: 15,
  },
  {
    id: 2,
    name: 'Overdue Task Escalation',
    description: 'Escalates overdue high priority tasks',
    franchiseId: 1,
    storeNumbers: null,
    isActive: true,
    trigger: 'TaskOverdue',
    conditionPair1: {
      id: 2,
      criteria1: { id: 2, field: 'TaskPriority', operator: 'Equals', value: 'High' },
      criteria2: { id: 3, field: 'TaskStatus', operator: 'NotEquals', value: 'Completed' },
      criteriaLogic: 'AND',
    },
    conditionPair2: null,
    conditionPairLogic: 'AND',
    action: { type: 'EscalateTask', params: { escalateToRoleId: '1', newPriority: 'Critical' } },
    createdAtUtc: '2024-10-15T09:00:00Z',
    createdByUserId: 1,
    updatedAtUtc: '2024-11-10T11:00:00Z',
    updatedByUserId: 2,
    lastTriggeredAtUtc: '2024-11-25T08:00:00Z',
    triggerCount: 42,
  },
  {
    id: 3,
    name: 'Low Stock Auto-Order',
    description: 'Creates order when stock falls below minimum',
    franchiseId: 1,
    storeNumbers: ['001', '002', '003'],
    isActive: false,
    trigger: 'StockLevelLow',
    conditionPair1: {
      id: 3,
      criteria1: { id: 4, field: 'StockLevel', operator: 'LessThan', value: '10' },
      criteria2: { id: 5, field: 'ProductCategory', operator: 'Equals', value: 'Beverages' },
      criteriaLogic: 'AND',
    },
    conditionPair2: {
      id: 4,
      criteria1: { id: 6, field: 'DayOfWeek', operator: 'NotEquals', value: 'Sunday' },
      criteria2: null,
      criteriaLogic: 'AND',
    },
    conditionPairLogic: 'AND',
    action: { type: 'CreateOrder', params: { orderTypeId: '1' } },
    createdAtUtc: '2024-09-01T08:00:00Z',
    createdByUserId: 1,
    updatedAtUtc: null,
    updatedByUserId: null,
    lastTriggeredAtUtc: null,
    triggerCount: 0,
  },
  // ---- Vapiano Workflows (franchiseId: 10) ----
  {
    id: 10,
    name: 'HACCP Temperature Breach Alert',
    description: 'Immediately alerts the store manager and head chef when a walk-in chiller or freezer temperature goes out of safe range',
    franchiseId: 10,
    storeNumbers: null,
    isActive: true,
    trigger: 'TemperatureOutOfRange',
    conditionPair1: {
      id: 10,
      criteria1: { id: 10, field: 'TemperatureZone', operator: 'In', value: 'Walk-in Cooler,Walk-in Freezer' },
      criteria2: null,
      criteriaLogic: 'AND',
    },
    conditionPair2: null,
    conditionPairLogic: 'AND',
    action: { type: 'SendNotification', params: { title: 'HACCP Alert: Temperature Breach', message: 'Temperature in {{TemperatureZone}} is {{Temperature}}°C — outside safe range. Investigate immediately.', recipientRoleIds: '1,7' } },
    createdAtUtc: '2024-02-01T10:00:00Z',
    createdByUserId: 10,
    updatedAtUtc: null,
    updatedByUserId: null,
    lastTriggeredAtUtc: '2024-11-18T06:45:00Z',
    triggerCount: 8,
  },
  {
    id: 11,
    name: 'Pasta Dough Overdue Escalation',
    description: 'Escalates to head chef if morning pasta dough preparation task is not completed by 9:30am',
    franchiseId: 10,
    storeNumbers: null,
    isActive: true,
    trigger: 'TaskOverdue',
    conditionPair1: {
      id: 11,
      criteria1: { id: 11, field: 'TaskCategory', operator: 'Equals', value: 'Pasta Station' },
      criteria2: { id: 12, field: 'TaskPriority', operator: 'In', value: 'High,Emergency' },
      criteriaLogic: 'AND',
    },
    conditionPair2: null,
    conditionPairLogic: 'AND',
    action: { type: 'EscalateTask', params: { escalateToRoleId: '7', newPriority: 'Emergency' } },
    createdAtUtc: '2024-02-15T09:00:00Z',
    createdByUserId: 10,
    updatedAtUtc: null,
    updatedByUserId: null,
    lastTriggeredAtUtc: '2024-11-22T09:35:00Z',
    triggerCount: 12,
  },
  {
    id: 12,
    name: 'Low Fresh Ingredient Auto-Order',
    description: 'Automatically creates an emergency order when fresh pasta ingredients (eggs, flour, guanciale, Pecorino) fall below minimum stock levels',
    franchiseId: 10,
    storeNumbers: null,
    isActive: true,
    trigger: 'StockLevelCritical',
    conditionPair1: {
      id: 12,
      criteria1: { id: 13, field: 'ProductCategory', operator: 'In', value: 'Fresh Dairy,Flour & Dry Goods,Italian Charcuterie,Italian Cheese' },
      criteria2: null,
      criteriaLogic: 'AND',
    },
    conditionPair2: null,
    conditionPairLogic: 'AND',
    action: { type: 'CreateOrder', params: { orderTypeId: '4' } },
    createdAtUtc: '2024-03-01T08:00:00Z',
    createdByUserId: 10,
    updatedAtUtc: '2024-06-01T10:00:00Z',
    updatedByUserId: 11,
    lastTriggeredAtUtc: '2024-11-24T07:00:00Z',
    triggerCount: 34,
  },
  {
    id: 13,
    name: 'Herb Garden Task Creation (Mon/Wed/Fri)',
    description: 'Creates herb garden watering task every Monday, Wednesday, and Friday at 6:30am for the opening team',
    franchiseId: 10,
    storeNumbers: null,
    isActive: true,
    trigger: 'ScheduledTime',
    conditionPair1: {
      id: 13,
      criteria1: { id: 14, field: 'DayOfWeek', operator: 'In', value: 'Monday,Wednesday,Friday' },
      criteria2: { id: 15, field: 'TimeOfDay', operator: 'Equals', value: '06:30' },
      criteriaLogic: 'AND',
    },
    conditionPair2: null,
    conditionPairLogic: 'AND',
    action: { type: 'CreateTask', params: { taskTemplateId: '1', assignToRoleId: '7', priority: 'Medium', title: 'Herb Garden Morning Watering' } },
    createdAtUtc: '2024-01-20T10:00:00Z',
    createdByUserId: 11,
    updatedAtUtc: null,
    updatedByUserId: null,
    lastTriggeredAtUtc: '2024-11-25T06:30:00Z',
    triggerCount: 128,
  },
  {
    id: 14,
    name: 'High Waste Alert — Notify Operations Manager',
    description: 'Sends notification to operations when food waste exceeds £75 in a single recording (common with pasta/pizza prep waste)',
    franchiseId: 10,
    storeNumbers: null,
    isActive: true,
    trigger: 'WasteRecorded',
    conditionPair1: {
      id: 14,
      criteria1: { id: 16, field: 'WasteValue', operator: 'GreaterThan', value: '75' },
      criteria2: null,
      criteriaLogic: 'AND',
    },
    conditionPair2: null,
    conditionPairLogic: 'AND',
    action: { type: 'SendNotification', params: { title: 'High Waste Alert', message: 'Waste of £{{WasteValue}} recorded at {{StoreName}}. Category: {{WasteCategory}}. Please review.', recipientRoleIds: '5' } },
    createdAtUtc: '2024-03-10T10:00:00Z',
    createdByUserId: 10,
    updatedAtUtc: null,
    updatedByUserId: null,
    lastTriggeredAtUtc: '2024-11-21T20:15:00Z',
    triggerCount: 22,
  },
  // ---- Five Guys Workflows (franchiseId: 11) ----
  {
    id: 20,
    name: 'Peanut Oil Temperature Alert',
    description: 'Critical alert when fryer peanut oil temperature drops below safe frying range (below 325°F / 163°C) or exceeds maximum (above 400°F / 204°C)',
    franchiseId: 11,
    storeNumbers: null,
    isActive: true,
    trigger: 'TemperatureOutOfRange',
    conditionPair1: {
      id: 20,
      criteria1: { id: 20, field: 'TemperatureZone', operator: 'Equals', value: 'Fry Station' },
      criteria2: null,
      criteriaLogic: 'AND',
    },
    conditionPair2: null,
    conditionPairLogic: 'AND',
    action: { type: 'SendNotification', params: { title: 'CRITICAL: Fryer Oil Temperature Alert', message: 'Peanut oil at {{Temperature}}°C is outside safe range (163-204°C). Stop frying immediately and investigate.', recipientRoleIds: '1,3' } },
    createdAtUtc: '2024-01-15T10:00:00Z',
    createdByUserId: 20,
    updatedAtUtc: null,
    updatedByUserId: null,
    lastTriggeredAtUtc: '2024-11-19T13:20:00Z',
    triggerCount: 5,
  },
  {
    id: 21,
    name: 'Potato Delivery Not Receipted Alert',
    description: 'Alerts store manager if the daily potato delivery has not been receipted by 9am — potatoes are critical to operations',
    franchiseId: 11,
    storeNumbers: null,
    isActive: true,
    trigger: 'ScheduledTime',
    conditionPair1: {
      id: 21,
      criteria1: { id: 21, field: 'TimeOfDay', operator: 'Equals', value: '09:00' },
      criteria2: { id: 22, field: 'DayOfWeek', operator: 'NotEquals', value: 'Sunday' },
      criteriaLogic: 'AND',
    },
    conditionPair2: null,
    conditionPairLogic: 'AND',
    action: { type: 'SendNotification', params: { title: 'Potato Delivery Check', message: 'Has today\'s potato delivery been received and inspected? Check delivery bay and receipt in system.', recipientRoleIds: '1' } },
    createdAtUtc: '2024-02-01T10:00:00Z',
    createdByUserId: 20,
    updatedAtUtc: null,
    updatedByUserId: null,
    lastTriggeredAtUtc: '2024-11-25T09:00:00Z',
    triggerCount: 187,
  },
  {
    id: 22,
    name: 'Secret Shopper Low Score Escalation',
    description: 'When a task related to secret shopper scoring is completed with a failing grade, escalate to area manager immediately',
    franchiseId: 11,
    storeNumbers: null,
    isActive: true,
    trigger: 'TaskCompleted',
    conditionPair1: {
      id: 22,
      criteria1: { id: 23, field: 'TaskCategory', operator: 'Equals', value: 'Customer Experience' },
      criteria2: { id: 24, field: 'TaskPriority', operator: 'In', value: 'High,Emergency' },
      criteriaLogic: 'AND',
    },
    conditionPair2: null,
    conditionPairLogic: 'AND',
    action: { type: 'EscalateTask', params: { escalateToRoleId: '5', newPriority: 'Emergency' } },
    createdAtUtc: '2024-03-01T09:00:00Z',
    createdByUserId: 21,
    updatedAtUtc: null,
    updatedByUserId: null,
    lastTriggeredAtUtc: '2024-11-15T16:00:00Z',
    triggerCount: 6,
  },
  {
    id: 23,
    name: 'Beef Patty Stock Critical — Emergency Order',
    description: 'Creates emergency order for fresh beef patties when stock falls to critical level. Five Guys never uses frozen patties.',
    franchiseId: 11,
    storeNumbers: null,
    isActive: true,
    trigger: 'StockLevelCritical',
    conditionPair1: {
      id: 23,
      criteria1: { id: 25, field: 'ProductCategory', operator: 'Equals', value: 'Fresh Beef' },
      criteria2: null,
      criteriaLogic: 'AND',
    },
    conditionPair2: null,
    conditionPairLogic: 'AND',
    action: { type: 'CreateOrder', params: { orderTypeId: '4' } },
    createdAtUtc: '2024-01-20T08:00:00Z',
    createdByUserId: 20,
    updatedAtUtc: '2024-05-15T10:00:00Z',
    updatedByUserId: 21,
    lastTriggeredAtUtc: '2024-11-23T11:00:00Z',
    triggerCount: 18,
  },
  {
    id: 24,
    name: 'Grill Temperature Compliance Check',
    description: 'When grill calibration task is completed, send results to operations for compliance records. All patties must reach 160°F internal.',
    franchiseId: 11,
    storeNumbers: null,
    isActive: true,
    trigger: 'TaskCompleted',
    conditionPair1: {
      id: 24,
      criteria1: { id: 26, field: 'TaskCategory', operator: 'Equals', value: 'Grill Station' },
      criteria2: null,
      criteriaLogic: 'AND',
    },
    conditionPair2: null,
    conditionPairLogic: 'AND',
    action: { type: 'SendNotification', params: { title: 'Grill Calibration Completed', message: 'Grill calibration task completed at {{StoreName}}. Review evidence for temperature compliance.', recipientRoleIds: '5' } },
    createdAtUtc: '2024-02-10T10:00:00Z',
    createdByUserId: 21,
    updatedAtUtc: null,
    updatedByUserId: null,
    lastTriggeredAtUtc: '2024-11-25T09:45:00Z',
    triggerCount: 245,
  },
];

/* ---- Reference Data ---- */

export const mockUsers: WorkflowUserDto[] = [
  { id: 1, name: 'John Smith', email: 'john.smith@example.com' },
  { id: 2, name: 'Sarah Johnson', email: 'sarah.johnson@example.com' },
  { id: 3, name: 'Mike Wilson', email: 'mike.wilson@example.com' },
  { id: 4, name: 'Emily Brown', email: 'emily.brown@example.com' },
  { id: 5, name: 'David Lee', email: 'david.lee@example.com' },
  { id: 6, name: 'Jessica Davis', email: 'jessica.davis@example.com' },
  { id: 7, name: 'Chris Martinez', email: 'chris.martinez@example.com' },
  { id: 8, name: 'Amanda Taylor', email: 'amanda.taylor@example.com' },
];

export const mockRoles: WorkflowRoleDto[] = [
  { id: 1, name: 'Store Manager', description: 'Full store management access' },
  { id: 2, name: 'Assistant Manager', description: 'Assistant management duties' },
  { id: 3, name: 'Team Leader', description: 'Shift supervision' },
  { id: 4, name: 'Team Member', description: 'Standard team member access' },
  { id: 5, name: 'Area Manager', description: 'Multi-store oversight' },
  { id: 6, name: 'Regional Director', description: 'Regional operations' },
  { id: 7, name: 'Kitchen Staff', description: 'Kitchen operations access' },
  { id: 8, name: 'Front of House', description: 'Customer-facing operations' },
];

export const mockOrderTypes: WorkflowOrderTypeDto[] = [
  { id: 1, name: 'Standard Order' },
  { id: 2, name: 'Rush Order' },
  { id: 3, name: 'Auto-replenishment' },
  { id: 4, name: 'Emergency Stock' },
  { id: 5, name: 'Promotional Stock' },
];

export const mockSuppliers: WorkflowSupplierDto[] = [
  { id: 1, name: 'Fresh Foods Direct', code: 'FFD001' },
  { id: 2, name: 'Beverage Wholesalers', code: 'BEV002' },
  { id: 3, name: 'Packaging Solutions', code: 'PKG003' },
  { id: 4, name: 'Dairy Distributors', code: 'DAI004' },
  { id: 5, name: 'Bakery Supplies Co', code: 'BAK005' },
  { id: 6, name: 'Meat & Poultry Ltd', code: 'MPL006' },
  { id: 7, name: 'Frozen Foods Inc', code: 'FFI007' },
];

export const mockStockLists: WorkflowStockListDto[] = [
  { id: 1, name: 'Full Inventory Count' },
  { id: 2, name: 'Perishables Only' },
  { id: 3, name: 'High Value Items' },
  { id: 4, name: 'Fast Moving Stock' },
  { id: 5, name: 'Seasonal Items' },
];

export const mockZones: WorkflowZoneDto[] = [
  { id: 1, name: 'Walk-in Cooler' },
  { id: 2, name: 'Walk-in Freezer' },
  { id: 3, name: 'Dry Storage' },
  { id: 4, name: 'Front Counter' },
  { id: 5, name: 'Display Fridge' },
  { id: 6, name: 'Kitchen' },
  { id: 7, name: 'Beverage Station' },
];

export const mockReportTypes: WorkflowReportTypeDto[] = [
  { id: 'sales-summary', name: 'Sales Summary' },
  { id: 'inventory-status', name: 'Inventory Status' },
  { id: 'waste-analysis', name: 'Waste Analysis' },
  { id: 'task-completion', name: 'Task Completion' },
  { id: 'stock-variance', name: 'Stock Variance' },
  { id: 'order-history', name: 'Order History' },
  { id: 'compliance-audit', name: 'Compliance Audit' },
];

export const mockTaskTemplates: WorkflowTaskTemplateDto[] = [
  { id: 1, name: 'Daily Cleaning', category: 'Cleaning' },
  { id: 2, name: 'Open Store Checklist', category: 'Opening Procedures' },
  { id: 3, name: 'Close Store Checklist', category: 'Closing Procedures' },
  { id: 4, name: 'Temperature Log', category: 'Food Safety' },
  { id: 5, name: 'Stock Rotation Check', category: 'Inventory' },
  { id: 6, name: 'Equipment Inspection', category: 'Equipment Maintenance' },
  { id: 7, name: 'Till Reconciliation', category: 'Cash Handling' },
  { id: 8, name: 'Food Safety Walkthrough', category: 'Food Safety' },
  { id: 9, name: 'Prep Area Sanitization', category: 'Cleaning' },
  { id: 10, name: 'Waste Log Entry', category: 'Waste Management' },
];

/* ---- ID Generator ---- */

export const idGenerators = {
  nextWorkflowId: 4,
};
